/* caleums-events.js - Caleums funnel UI-intent tracer (CEL-14).
 * Emits UI-intent events to PostHog: product_card_click, hero_cta_click, nav_category_click,
 * whatsapp_consult_click, add_to_cart_click (product handle only) and cart_drawer_opened.
 * Confirmed commerce/revenue events stay with the Shopify Web Pixel.
 *
 * Privacy rules (security review, 2026-10-05):
 * - Fail closed: nothing is sent unless Shopify's Customer Privacy API is loaded AND
 *   analyticsProcessingAllowed() is true at the moment of the event. Consent is re-read on every
 *   event, and visitorConsentCollected re-checks it, so a withdrawal stops sending immediately.
 * - No global dimensions: no posthog.register super-properties, nothing read from the URL, query
 *   string, referrer, localStorage or DOM text. Super-properties left by earlier versions of this
 *   file (theme_role, qa_run_id, page_type, viewport) are unregistered on load.
 * - Event-scoped allowlist only: handles from Liquid-rendered data-* attributes (validated, max 100
 *   chars), card position (integer), hero CTA from a fixed set, page_type/theme_role/viewport from
 *   fixed formats. Any other value is dropped.
 * The click listener is bound once at document level, so it survives Section Rendering re-renders. */
(function (root, factory) {
  var api = factory();
  if (typeof module !== "undefined" && module.exports) {
    module.exports = api; // test harness
  } else {
    api.autoInit(root); // browser
  }
})(typeof self !== "undefined" ? self : this, function () {
  var LEGACY_SUPER_PROPERTIES = ["theme_role", "qa_run_id", "page_type", "viewport"];
  var HERO_CTAS = ["shop", "bespoke"];

  function handle(value) {
    var v = typeof value === "string" ? value.trim().toLowerCase() : "";
    return /^[a-z0-9][a-z0-9_-]{0,99}$/.test(v) ? v : undefined;
  }

  function oneOf(value, allowed) {
    var v = typeof value === "string" ? value.trim().toLowerCase() : "";
    return allowed.indexOf(v) !== -1 ? v : undefined;
  }

  function position(value) {
    var n = parseInt(value, 10);
    return n >= 1 && n <= 999 ? n : undefined;
  }

  function clean(props) {
    var out = {};
    for (var key in props) {
      if (Object.prototype.hasOwnProperty.call(props, key) && props[key] !== undefined) out[key] = props[key];
    }
    return out;
  }

  function createTracker(env) {
    var doc = env.document;
    var root = doc.documentElement;

    function context() {
      return {
        theme_role: oneOf(root.getAttribute("data-caleums-theme-role"), ["live", "staging"]),
        page_type: handle(root.getAttribute("data-caleums-page-type")),
        viewport: (env.window.innerWidth || 1440) < 768 ? "mobile" : "desktop",
      };
    }

    function analyticsAllowed() {
      try {
        var privacy = env.window.Shopify && env.window.Shopify.customerPrivacy;
        return !!(privacy && typeof privacy.analyticsProcessingAllowed === "function" && privacy.analyticsProcessingAllowed() === true);
      } catch (e) {
        return false;
      }
    }

    function dropLegacySuperProperties() {
      var ph = env.window.posthog;
      if (!ph || typeof ph.unregister !== "function") return;
      for (var i = 0; i < LEGACY_SUPER_PROPERTIES.length; i++) {
        try { ph.unregister(LEGACY_SUPER_PROPERTIES[i]); } catch (e) {}
      }
    }

    function loadPrivacy() {
      var shopify = env.window.Shopify;
      if (shopify && !shopify.customerPrivacy && typeof shopify.loadFeatures === "function") {
        shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], function () {});
      }
    }

    function capture(event, props) {
      if (!analyticsAllowed()) return;
      if (env.window.CaleumsPostHogConsent && !env.window.CaleumsPostHogConsent.ready()) return;
      var ph = env.window.posthog;
      if (!ph || typeof ph.capture !== "function") return;
      var c = context();
      ph.capture(event, clean({
        theme_role: c.theme_role,
        page_type: c.page_type,
        viewport: c.viewport,
        collection: props.collection,
        position: props.position,
        cta: props.cta,
        category: props.category,
        product: props.product,
      }));
    }

    function onClick(e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var el;
      if ((el = t.closest("[data-caleums-card]"))) {
        capture("product_card_click", { collection: handle(el.getAttribute("data-collection")), position: position(el.getAttribute("data-position")) });
      } else if ((el = t.closest("[data-caleums-hero-cta]"))) {
        capture("hero_cta_click", { cta: oneOf(el.getAttribute("data-cta"), HERO_CTAS) });
      } else if ((el = t.closest("[data-caleums-nav]"))) {
        capture("nav_category_click", { category: handle(el.getAttribute("data-category")) });
      } else if ((el = t.closest("[data-caleums-whatsapp]"))) {
        capture("whatsapp_consult_click", { product: handle(el.getAttribute("data-product")) });
      } else if ((el = t.closest("[data-caleums-atc]"))) {
        capture("add_to_cart_click", { product: handle(el.getAttribute("data-product")) });
      }
    }

    function watchCartDrawer() {
      // Prestige's <cart-drawer id="cart-drawer"> toggles the `open` attribute on show/hide.
      var drawer = doc.getElementById && doc.getElementById("cart-drawer");
      var MO = env.window.MutationObserver;
      if (!drawer || !MO) return;
      var edge = risingEdge(function () {
        capture("cart_drawer_opened", {});
      });
      edge(drawer.hasAttribute("open"));
      new MO(function () {
        edge(drawer.hasAttribute("open"));
      }).observe(drawer, { attributes: true, attributeFilter: ["open"] });
    }

    return {
      init: function () {
        dropLegacySuperProperties();
        if (env.window.__caleumsEventsBound) return;
        env.window.__caleumsEventsBound = true;
        doc.addEventListener("click", onClick);
        // Consent is re-read on every capture; this only re-runs the cleanup when consent changes.
        doc.addEventListener("visitorConsentCollected", dropLegacySuperProperties);
        loadPrivacy();
        watchCartDrawer();
      },
    };
  }

  function autoInit(win) {
    if (!win || !win.document) return;
    createTracker({ window: win, document: win.document }).init();
  }

  function risingEdge(fire) {
    var was = false;
    return function (isOpen) {
      if (isOpen && !was) fire();
      was = isOpen;
    };
  }

  return { createTracker: createTracker, autoInit: autoInit, risingEdge: risingEdge, handle: handle };
});
